/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Button } from '@mpb-com/feather/components/atoms/button';
import {
    Choice,
    ChoiceLabelSize,
    ChoiceVariant,
} from '@mpb-com/feather/components/atoms/choice';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { IconSize } from '@mpb-com/feather/components/atoms/icon';
import { Notification } from '@mpb-com/feather/components/atoms/notification';
import { NotificationVariant } from '@mpb-com/feather/components/atoms/notification/types';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
} from 'react';
import styled from 'styled-components';
import { object } from 'yup';

import {
    ApplicationContext,
    ModalSize,
    logger,
} from '@toucan/client/global/application';
import { FormValues } from '@toucan/client/global/axios';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { UserContext, email, password } from '@toucan/client/global/user';

import { login, sendVerification } from '@toucan/client/core/user';

// eslint-disable-next-line boundaries/element-types -- This should be using the feather input component
import { Input } from '@toucan/client/shared/components/input';
import { Link, LinkSize } from '@toucan/client/shared/components/link';
import { RequestPasswordReset } from '@toucan/client/shared/components/request-password-reset';
import { useCsrf } from '@toucan/client/shared/hooks/use-csrf';
import { useTranslatedHref } from '@toucan/client/shared/hooks/use-translated-href';

// eslint-disable-next-line boundaries/element-types -- This should be using the feather password input component
import { PasswordInput } from '@toucan/client/blocks/components/password-input';

/* eslint-disable-next-line boundaries/element-types -- This imports the signup funnel component and the signup imports the login form component :(
 * We should have a better way to handle this, maybe a parent component that handles the relationships between the components.
 */
import { SignUpFunnel } from '@toucan/client/blocks/components/sign-up-funnel';

import { styles } from './login-form.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface LoginFormProps {
    /**
     * Initial values for the form fields.
     *
     * @default See usage examples
     */
    initialValues?: FormValues;

    /**
     * Initial error message to display
     */
    initialStatus?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Callback for when the user is successfully logged in
     */
    onLogInSuccess?: () => void;

    /**
     * prompt is element which is displayed directly underneath the Login
     * heading.
     *
     * @default <p className="text-center">"Don't have an account?" <a>Join Now</a></p>
     */
    prompt?: ReactNode;

    /**
     * Whether to show the login in heading
     * @default true
     */
    showHeading?: boolean;
    headingSize?: HeadingSize;
}

const StyledLoginForm = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const LoginForm: FunctionComponent<LoginFormProps> = ({
    initialValues = {
        email: '',
        password: '',
        rememberMe: false,
    },
    prompt,
    initialStatus,
    onLogInSuccess,
    showHeading = true,
    headingSize = HeadingSize.H2,
    ...otherProps
}: LoginFormProps) => {
    const { csrfToken } = useCsrf();
    const { getUser } = useContext(UserContext);
    const { activeMarket } = useContext(MarketContext);
    const { setModalVisible, setModalContent, setModalSize } =
        useContext(ApplicationContext);
    const { t } = useTranslation();
    const validationSchema = object().shape({
        email: email(t),
        password: password(t),
    });

    const contactHref = useTranslatedHref('/help/contact-us');

    function handleOpenSignUp() {
        setModalSize(ModalSize.LARGE);
        setModalContent(<SignUpFunnel />);
        setModalVisible(true);
    }

    function handleOpenRequestPasswordReset(email: string) {
        setModalContent(<RequestPasswordReset initialEmail={email} />);
        setModalSize(ModalSize.SMALL);
        setModalVisible(true);
    }

    /**
     * Handle the sign up form submit. Calls the sign up service and transitions
     * the state machine on success or displays any errors.
     *
     * @param values {FormValues} the submitted form values
     * @param formActions {FormikHelpers<FormValues>} actions which can be performed on the formik object state
     */
    async function handleLogIn(
        values: FormValues,
        formActions: FormikHelpers<FormValues>
    ) {
        try {
            // disable form submission
            formActions.setSubmitting(true);

            // try to sign in
            await login(values, activeMarket.code, csrfToken);

            // try to get our user
            await getUser();

            onLogInSuccess && onLogInSuccess();
        } catch (error: any) {
            // re enable form submission
            formActions.setSubmitting(false);

            logger.error(error);
            if (error?.validationErrors) {
                formActions.setErrors(error.validationErrors);
            }

            switch (error?.response?.data?.errorCode) {
                case '001': {
                    formActions.setStatus(
                        <p>
                            <Trans
                                components={[
                                    <strong key={0}>$1</strong>,
                                    <Link
                                        key={1}
                                        onClick={() => {
                                            handleOpenRequestPasswordReset(
                                                values.email as string
                                            );
                                        }}
                                    >
                                        $2
                                    </Link>,
                                ]}
                            >
                                {t(
                                    "<0>Uh-oh.</0> Your password and/or email isn't right. Please try again or <1>reset your password</1>."
                                )}
                            </Trans>
                        </p>
                    );
                    break;
                }

                case '013': {
                    formActions.setStatus(
                        <Fragment>
                            <p>
                                <strong>{t('Better safe than sorry.')}</strong>
                            </p>
                            <p>
                                <Trans
                                    components={[<strong key={0}>$1</strong>]}
                                >
                                    {t(
                                        "We can't let you in yet. To keep your account secure, you must verify it first. Simply <0>tap the link we emailed you</0> when you signed up."
                                    )}
                                </Trans>
                            </p>
                            <p>
                                <Trans
                                    components={[
                                        <Link
                                            key={0}
                                            onClick={() => {
                                                sendVerification(
                                                    values.email as string,
                                                    activeMarket.code
                                                );
                                            }}
                                        >
                                            $1
                                        </Link>,
                                    ]}
                                >
                                    {t(
                                        "If you can't find it, we can <0>email you another verification link.</0>"
                                    )}
                                </Trans>
                            </p>
                        </Fragment>
                    );
                    break;
                }

                default:
                    formActions.setStatus(
                        <Fragment>
                            <p>
                                <strong>
                                    {t(
                                        "Something's gone wrong, but it's not your fault."
                                    )}
                                </strong>
                            </p>
                            <p>
                                <Trans
                                    components={[
                                        <WrappedLink
                                            key="get-in-touch"
                                            href={contactHref}
                                            passHref
                                        >
                                            $2
                                        </WrappedLink>,
                                    ]}
                                >
                                    {t(
                                        "Sorry, it looks like there's an error in our system. Please <0>get in touch</0> so we can fix it. Or, you can try again."
                                    )}
                                </Trans>
                            </p>
                        </Fragment>
                    );
            }
        }
    }

    return (
        <StyledLoginForm {...otherProps}>
            {showHeading ? (
                <Heading
                    data-testid="log-in-form__heading"
                    size={headingSize}
                    className="text-center"
                >
                    {t('Log in')}
                </Heading>
            ) : null}
            {prompt ? (
                prompt
            ) : (
                <p className="text-center">
                    {t("Don't have an account? ")}
                    <Link
                        key={0}
                        onClick={handleOpenSignUp}
                        data-testid="log-in-form__sign-up-button"
                    >
                        {t('Join now')}
                    </Link>
                </p>
            )}
            <Formik
                initialValues={initialValues}
                onSubmit={handleLogIn}
                validationSchema={validationSchema}
                initialStatus={initialStatus}
            >
                {({ errors, touched, status, isSubmitting, values }) => (
                    <Form className="grid-container" data-testid="log-in-form">
                        <Field name="email">
                            {({ field }: FieldProps) => (
                                <Input
                                    {...field}
                                    type="text"
                                    label={t('Email address')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="email-address"
                                    required={true}
                                    data-testid="log-in-form__email"
                                />
                            )}
                        </Field>
                        <Field name="password">
                            {({ field }: FieldProps) => (
                                <PasswordInput
                                    {...field}
                                    label={t('Password')}
                                    showLabel={t('Show')}
                                    hideLabel={t('Hide')}
                                    error={errors[field.name]}
                                    touched={touched[field.name]}
                                    className="password"
                                    required={true}
                                    data-testid="log-in-form__password"
                                />
                            )}
                        </Field>
                        <div className="forgot-password">
                            <Link
                                size={LinkSize.SMALL}
                                onClick={() => {
                                    handleOpenRequestPasswordReset(
                                        values.email as string
                                    );
                                }}
                                data-testid="log-in-form__request-password-reset-button"
                            >
                                {t('Forgot your password?')}
                            </Link>
                        </div>
                        <Field name="rememberMe">
                            {({ field }: FieldProps) => (
                                <div className="remember-me">
                                    <Choice
                                        {...field}
                                        label={t('Remember me')}
                                        value="true"
                                        variant={ChoiceVariant.CHECKBOX}
                                        checked={field.value}
                                        labelSize={ChoiceLabelSize.SMALL}
                                    />
                                </div>
                            )}
                        </Field>
                        <div className="submit">
                            {status && (
                                <Notification
                                    variant={NotificationVariant.ERROR}
                                    data-testid="log-in-form__error-message"
                                    className="error-message"
                                    iconSize={IconSize.L}
                                >
                                    {status}
                                </Notification>
                            )}
                            <Button
                                type="submit"
                                disabled={isSubmitting || !csrfToken}
                            >
                                {t('Log in')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </StyledLoginForm>
    );
};
